import { reportFailure } from "./telemetry";
import { useEffect, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import type { WorkspaceModel } from "./useWorkspace";
import { Modal } from "./Modal";
import { SheetPreview } from "./SheetPreview";
import { exportDrawing } from "./documents";
import { api, notify } from "./api";
import { overflowingSheetItems } from "./export-overflow";
import { tracingSheetPdf } from "./trace-export";
import type { Trace } from "../shared/schema";
import { sheetPageDimensionsMm } from "../shared/sheets";
export function ExportSetDialog({ model }: { model: WorkspaceModel }) {
  const { editor, sheets, room } = model;
  const traces = model.shapes.filter(
    (shape): shape is Trace =>
      shape.type === "trace" &&
      shape.parentId.startsWith("page:") &&
      !editor.isShapeHidden(shape),
  );
  const pages = [...sheets, ...traces];
  const savedKey = `calque:export-selection:${room.id}:${model.user.id}`;
  const [selected, setSelected] = useState(
    () => new Set(model.active ? [model.active.id] : []),
  );
  const [scaleOverrides, setScaleOverrides] = useState<Record<string, string>>(
    {},
  );
  const [hasSaved, setHasSaved] = useState(() => {
    try {
      return !!window.localStorage.getItem(savedKey);
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(""),
    [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const chosen = pages.filter((s) => selected.has(s.id));
  const overflow = new Map(
    sheets.map((sheet) => [sheet.id, overflowingSheetItems(editor, sheet)]),
  );
  const clipped = chosen
    .filter((page) => page.type === "sheet")
    .flatMap((sheet) => overflow.get(sheet.id) || []);
  const defaultScale = Number(model.scale) > 0 ? model.scale : "100";
  const sheetScale = (id: string) => Number(scaleOverrides[id] ?? defaultScale);
  const invalidScale = chosen.some(
    (page) =>
      page.type === "sheet" &&
      page.props.metersPerUnit > 0 &&
      (!Number.isFinite(sheetScale(page.id)) ||
        sheetScale(page.id) < 1 ||
        sheetScale(page.id) > 100000),
  );
  const exportSet = async () => {
    const abort = new AbortController();
    controller.current = abort;
    setError("");
    try {
      if (room.platform === "cloudflare")
        await api(`/api/rooms/${room.id}/export-access`, {});
      const result = await PDFDocument.create();
      for (let i = 0; i < chosen.length; i++) {
        abort.signal.throwIfAborted();
        setBusy(`Preparing sheet ${i + 1} of ${chosen.length}…`);
        const page = editor.getShape(chosen[i].id);
        if (page?.type !== "sheet" && page?.type !== "trace")
          throw new Error("A selected sheet was removed.");
        const bytes =
          page.type === "trace"
            ? await tracingSheetPdf(editor, page)
            : await exportDrawing(
                editor,
                page,
                page.props.paper === "A4" ? "A4" : "A3",
                page.props.metersPerUnit > 0 ? sheetScale(page.id) : 0,
                page.props.w >= page.props.h,
                { signal: abort.signal, returnBytes: true },
              );
        abort.signal.throwIfAborted();
        const one = await PDFDocument.load(bytes);
        const pages = await result.copyPages(one, one.getPageIndices());
        pages.forEach((p) => result.addPage(p));
      }
      abort.signal.throwIfAborted();
      const bytes = await result.save();
      abort.signal.throwIfAborted();
      const url = URL.createObjectURL(
        new Blob([bytes as Uint8Array<ArrayBuffer>], {
          type: "application/pdf",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${room.name}-review.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      notify("Review set exported in sheet order. Print at 100%.");
      model.setModal(null);
    } catch (e) {
      if (!abort.signal.aborted) {
        reportFailure("export", "export", e);
        setError((e as Error).message);
      }
    } finally {
      controller.current = null;
      setBusy("");
    }
  };
  return (
    <Modal
      title="Export review set"
      close={() => {
        controller.current?.abort();
        model.setModal(null);
      }}
    >
      <p>
        Select pages in presentation order. Tracing pages contain their artwork
        only. Content outside a paper boundary is omitted; discussions stay on
        the board.
      </p>
      <div className="export-selection-actions">
        <button
          type="button"
          disabled={!!busy}
          onClick={() => setSelected(new Set(pages.map((page) => page.id)))}
        >
          Select all
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => setSelected(new Set())}
        >
          Select none
        </button>
        <button
          type="button"
          disabled={!!busy || !selected.size}
          onClick={() => {
            try {
              window.localStorage.setItem(
                savedKey,
                JSON.stringify({ ids: [...selected], scaleOverrides }),
              );
              setHasSaved(true);
            } catch {
              setError("Cannot save this selection on this device.");
            }
          }}
        >
          Save selection
        </button>
        {hasSaved && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => {
              try {
                const saved = JSON.parse(
                  window.localStorage.getItem(savedKey) || "{}",
                );
                const available = new Set<string>(pages.map((page) => page.id));
                setSelected(
                  new Set(
                    Array.isArray(saved.ids)
                      ? saved.ids.filter(
                          (id: unknown) =>
                            typeof id === "string" && available.has(id),
                        )
                      : [],
                  ),
                );
                setScaleOverrides(
                  saved.scaleOverrides &&
                    typeof saved.scaleOverrides === "object"
                    ? saved.scaleOverrides
                    : {},
                );
              } catch {
                setError("Saved selection could not be read.");
              }
            }}
          >
            Load saved
          </button>
        )}
      </div>
      <div className="export-sheet-list">
        {pages.map((s) => {
          const paper =
            s.type === "sheet"
              ? sheetPageDimensionsMm(
                  s,
                  s.props.paper === "A4" ? "A4" : "A3",
                  s.props.w >= s.props.h,
                )
              : null;
          return (
            <div className="export-sheet-entry" key={s.id}>
              <label>
                <input
                  type="checkbox"
                  disabled={!!busy}
                  checked={selected.has(s.id)}
                  onChange={(e) =>
                    setSelected((old) => {
                      const next = new Set(old);
                      e.target.checked ? next.add(s.id) : next.delete(s.id);
                      return next;
                    })
                  }
                />
                <SheetPreview editor={editor} sheet={s} />
                <span>
                  {s.props.name}
                  <small>
                    {s.type === "trace"
                      ? "Tracing · A3 · artwork only"
                      : `${s.props.paper} · ${Math.round(paper!.widthMm)} × ${Math.round(paper!.heightMm)} mm · ${s.props.metersPerUnit ? `1:${sheetScale(s.id)}` : "Not to Scale"}`}
                  </small>
                  {s.type === "sheet" && !!overflow.get(s.id)?.length && (
                    <strong className="export-overflow-count">
                      {overflow.get(s.id)!.length} item
                      {overflow.get(s.id)!.length === 1 ? "" : "s"} crosses the
                      paper edge
                    </strong>
                  )}
                </span>
              </label>
              {s.type === "sheet" &&
                s.props.metersPerUnit > 0 &&
                selected.has(s.id) && (
                  <label className="export-sheet-scale">
                    Output scale · 1:
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      step="1"
                      disabled={!!busy}
                      aria-label={`Export scale for ${s.props.name}`}
                      value={scaleOverrides[s.id] ?? defaultScale}
                      onChange={(event) =>
                        setScaleOverrides((old) => ({
                          ...old,
                          [s.id]: event.target.value,
                        }))
                      }
                    />
                  </label>
                )}
            </div>
          );
        })}
      </div>
      {chosen.some((s) => s.type === "sheet" && s.props.metersPerUnit > 0) && (
        <label>
          Calibrated drawing export scale · 1:
          <input
            type="number"
            min="1"
            value={defaultScale}
            disabled={!!busy}
            onChange={(e) => model.setScale(e.target.value)}
          />
        </label>
      )}
      <small className="export-note">
        Unscaled sheets fit their selected paper size. Imported drawings include
        a scale-status footer; uncalibrated drawings are labeled Not to Scale.
      </small>
      {chosen.length > 20 && (
        <small role="alert">Choose up to 20 pages for one review set.</small>
      )}
      {!!clipped.length && (
        <div className="export-overflow-warning" role="status">
          <div>
            <strong>
              {clipped.length} selected item{clipped.length === 1 ? "" : "s"}{" "}
              will be clipped at the paper edge.
            </strong>
            <small>
              Return to the sheet to reposition it, or export anyway.
            </small>
          </div>
          <button
            onClick={() => {
              const sheet = chosen.find(
                (page) =>
                  page.type === "sheet" && !!overflow.get(page.id)?.length,
              );
              if (sheet?.type === "sheet") {
                model.setModal(null);
                model.focusSheet(sheet);
              }
            }}
          >
            Return to sheet
          </button>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      {busy ? (
        <>
          <p role="status">{busy}</p>
          <button onClick={() => controller.current?.abort()}>
            Cancel export
          </button>
        </>
      ) : (
        <button
          className="primary"
          disabled={!chosen.length || chosen.length > 20 || invalidScale}
          onClick={() => void exportSet()}
        >
          {clipped.length ? "Export anyway" : "Download"} · {chosen.length} page
          {chosen.length === 1 ? "" : "s"}
        </button>
      )}
    </Modal>
  );
}
